import { useCallback, useRef, useState } from "react";

const BEFORE = [
  { q: "Did the client confirm tomorrow?", a: "You check your texts manually." },
  { q: "Can someone answer this DM?", a: "You, at 9pm, again." },
  { q: "Did we follow up with that lead?", a: "Nobody knows. Lead is gone." },
  { q: "Can you write the caption?", a: "You, staring at a blank Notes app." },
  { q: "What's our cancellation policy again?", a: "You, typing the same paragraph for the 40th time." },
];

const AFTER = [
  { q: "Did the client confirm tomorrow?", a: "Already reminded, twice, automatically." },
  { q: "Can someone answer this DM?", a: "Already answered, in your tone of voice." },
  { q: "Did we follow up with that lead?", a: "Every time, on schedule, without fail." },
  { q: "Can you write the caption?", a: "Drafted. You just hit approve." },
  { q: "What's our cancellation policy again?", a: "Answered instantly, correctly, forever." },
];

const Panel = ({ data, tone }) => (
  <div className="p-7 sm:p-10">
    <div className="flex items-center gap-3 mb-8">
      <span className={`h-1.5 w-1.5 rounded-full ${tone === "after" ? "bg-[#5CCBF5]" : "bg-[#d8b58a]"}`} />
      <span className="font-mono text-[11px] tracking-[0.24em] uppercase text-[#a7a7a2]">
        {tone === "after" ? "After the Checkup" : "Before the Checkup"}
      </span>
    </div>
    <div className="space-y-6">
      {data.map((row, i) => (
        <div key={i} className="grid grid-cols-1 gap-1">
          <p className="text-[#f5f3ee] text-base sm:text-lg leading-snug">
            <span className="font-mono text-[10px] tracking-widest text-[#5c5c58] mr-2">YOU</span>
            "{row.q}"
          </p>
          <p className={`text-sm sm:text-base leading-snug ${tone === "after" ? "text-[#5CCBF5]" : "text-[#a7a7a2]"}`}>
            <span className="font-mono text-[10px] tracking-widest text-[#5c5c58] mr-2">{tone === "after" ? "HANDLED" : "REALITY"}</span>
            {tone === "after" ? `"${row.a}"` : row.a}
          </p>
        </div>
      ))}
    </div>
  </div>
);

export const BeforeAfterSlider = () => {
  const ref = useRef(null);
  const [pct, setPct] = useState(50);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPct(Math.max(4, Math.min(96, p)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    setFromClientX(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const onUp = () => (dragging.current = false);

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden border border-white/10 bg-[#131C31] select-none touch-none"
      data-testid="before-after-slider"
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchMove={onMove}
      onTouchEnd={onUp}
    >
      {/* AFTER underneath (full) */}
      <div className="w-full">
        <Panel data={AFTER} tone="after" />
      </div>

      {/* BEFORE on top, clipped */}
      <div
        className="absolute inset-0 bg-[#0E1526] overflow-hidden"
        style={{ width: `${pct}%` }}
      >
        <div style={{ width: ref.current ? ref.current.getBoundingClientRect().width : "100%" }}>
          <Panel data={BEFORE} tone="before" />
        </div>
      </div>

      {/* Handle */}
      <div
        className="absolute top-0 bottom-0 z-20 flex items-center justify-center cursor-ew-resize"
        style={{ left: `calc(${pct}% - 20px)`, width: 40 }}
        onMouseDown={onDown}
        onTouchStart={onDown}
        data-testid="slider-handle"
      >
        <div className="absolute top-0 bottom-0 w-px bg-white/25" />
        <div className="relative h-10 w-10 rounded-full bg-[#f5f3ee] text-[#0E1526] flex items-center justify-center shadow-lg">
          <span className="font-mono text-[13px] leading-none">↔</span>
        </div>
      </div>

      {/* Corner tags */}
      <span className="absolute bottom-4 left-5 z-10 font-mono text-[10px] tracking-[0.2em] uppercase text-[#5c5c58]">Before</span>
      <span className="absolute bottom-4 right-5 z-10 font-mono text-[10px] tracking-[0.2em] uppercase text-[#5CCBF5]">After</span>
    </div>
  );
};

export default BeforeAfterSlider;
